import { type TablaExport, exportarExcel, exportarPDF, exportarPDFTablas } from '@/lib/exportar';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { type RefObject, useRef, useState } from 'react';
import { Button } from '../ui/button';

interface Props {
  titulo: string;
  subtitulo: string;
  tablas: TablaExport[];
  refContenido?: RefObject<HTMLElement | null>;
  disabled?: boolean;
  /**
   * Hook ejecutado ANTES de exportar (PDF o Excel). Usar para cargar datos
   * completos que afectan tanto a la captura DOM como a las tablas planas
   * (ej. fetch de todos los registros cuando hay paginación).
   * Tras resolverse, esperamos un doble requestAnimationFrame para que React
   * commitee los setStates antes de leer la prop `tablas` actualizada.
   */
  onAntesExportar?: () => Promise<void> | void;
  /** Hook ejecutado DESPUÉS de cualquier exportación, para restaurar estado. */
  onDespuesExportar?: () => void;
  /**
   * Hook ejecutado ANTES de capturar el PDF (después de `onAntesExportar`).
   * Usar para cosas que SOLO afectan al PDF: expandir filas agrupadas,
   * ocultar elementos visuales, etc.
   */
  onAntesExportarPDF?: () => Promise<void> | void;
  /** Hook ejecutado DESPUÉS de la captura del PDF (antes de `onDespuesExportar`). */
  onDespuesExportarPDF?: () => void;
}

export function BotonExportar({
  titulo,
  subtitulo,
  tablas,
  refContenido,
  disabled,
  onAntesExportar,
  onDespuesExportar,
  onAntesExportarPDF,
  onDespuesExportarPDF,
}: Props) {
  const [exportandoPDF, setExportandoPDF] = useState(false);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const nombreArchivo = `${titulo} - ${subtitulo}`;

  // Ref actualizada cada render. Cuando un handler está in-flight y
  // `onAntesExportar` dispara setStates en el padre, el render produce
  // un NUEVO `tablas` prop, pero el closure del handler todavía tiene el
  // viejo. Leemos `tablasRef.current` después del doble rAF para obtener
  // las tablas frescas reflejando la data recién cargada.
  const tablasRef = useRef(tablas);
  tablasRef.current = tablas;

  // Doble requestAnimationFrame: garantiza que React commiteó el render
  // tras los setStates ejecutados dentro de `onAntesExportar`. Wrapper
  // explícito porque rAF retorna number y el resolver de Promise<void> espera void.
  function esperarReflowReact(): Promise<void> {
    return new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }

  async function handleExcel() {
    setExportandoExcel(true);
    try {
      if (onAntesExportar) {
        await onAntesExportar();
        await esperarReflowReact();
      }
      exportarExcel(nombreArchivo, tablasRef.current);
    } finally {
      onDespuesExportar?.();
      setExportandoExcel(false);
    }
  }

  async function handlePDF() {
    setExportandoPDF(true);
    try {
      if (onAntesExportar) {
        await onAntesExportar();
        await esperarReflowReact();
      }
      if (onAntesExportarPDF) {
        await onAntesExportarPDF();
        await esperarReflowReact();
      }
      if (refContenido?.current) {
        await exportarPDF(titulo, subtitulo, refContenido.current);
      } else {
        await exportarPDFTablas(titulo, subtitulo, tablasRef.current);
      }
    } finally {
      onDespuesExportarPDF?.();
      onDespuesExportar?.();
      setExportandoPDF(false);
    }
  }

  return (
    <div className="flex items-center gap-1">
      <span className="mr-1 flex items-center gap-1 text-xs text-muted-foreground">
        <Download className="h-3.5 w-3.5" />
        Exportar
      </span>
      <Button
        size="sm"
        variant="outline"
        disabled={disabled || exportandoExcel}
        onClick={handleExcel}
        title="Exportar a Excel"
      >
        {exportandoExcel ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <FileSpreadsheet className="h-3.5 w-3.5" />
        )}
        Excel
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={disabled || exportandoPDF}
        onClick={handlePDF}
        title="Exportar a PDF"
      >
        {exportandoPDF ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <FileText className="h-3.5 w-3.5" />
        )}
        PDF
      </Button>
    </div>
  );
}
